/** ISO 3166-1 alpha-2 country data for the shared CountrySelector.
 *
 * We store ONLY the codes — display names are derived at render time via
 * `Intl.DisplayNames` in the active UI locale (so "DE" shows as "Germany"
 * in English, "Allemagne" in French, etc.), and flags are computed from
 * the code as Unicode regional-indicator emoji (no asset/dependency).
 *
 * `DEFAULT_COUNTRY` = France: the product's primary market, used as the
 * default wherever a country is collected (signup, new address, …).
 */
export const DEFAULT_COUNTRY = "FR";

/** Shown first in the list (most-relevant markets), in this order. */
export const PRIORITY_COUNTRIES = [
  "FR",
  "ES",
  "BE",
  "CH",
  "DE",
  "IT",
  "PT",
  "NL",
  "LU",
  "GB",
  "US",
];

/** Full ISO 3166-1 alpha-2 set. */
export const COUNTRY_CODES: string[] = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AR", "AT", "AU", "AW",
  "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL",
  "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BW", "BY", "BZ", "CA", "CD",
  "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV",
  "CW", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE", "EG",
  "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB",
  "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR",
  "GT", "GU", "GW", "GY", "HK", "HN", "HR", "HT", "HU", "ID", "IE", "IL",
  "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM", "JO", "JP", "KE",
  "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB",
  "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD",
  "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ", "MR",
  "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA", "NC", "NE", "NF",
  "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF",
  "PG", "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA",
  "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC", "SD", "SE", "SG", "SH",
  "SI", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY",
  "SZ", "TC", "TD", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR",
  "TT", "TV", "TW", "TZ", "UA", "UG", "US", "UY", "UZ", "VA", "VC", "VE",
  "VG", "VI", "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
];

const _CODE_SET = new Set(COUNTRY_CODES);

export function isValidCountry(code: string): boolean {
  return _CODE_SET.has((code || "").toUpperCase());
}

/** Convert a 2-letter ISO code to its flag emoji (regional indicators). */
export function flagEmoji(code: string): string {
  const cc = (code || "").toUpperCase();
  if (cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return "🏳️";
  const base = 0x1f1e6; // 🇦
  return String.fromCodePoint(
    base + cc.charCodeAt(0) - 65,
    base + cc.charCodeAt(1) - 65,
  );
}

/** Localised country name for a code in the given locale (falls back to
 * the raw code when Intl.DisplayNames is unavailable). */
export function countryName(code: string, locale = "en"): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" });
    return dn.of((code || "").toUpperCase()) || code;
  } catch {
    return code;
  }
}
